import { solvePow, type SolveResult } from './solver';
import type { PowWorkerRequest, PowWorkerResponse } from './pow-worker';

/**
 * Виносить підбір nonce у Web Worker.
 *
 * У головному потоці цей цикл підвішує інтерфейс рівно перед дзвінком — там,
 * де користувач найменше готовий до завмирання. Якщо Worker недоступний
 * (старий браузер, тест), обчислення виконується на місці: краще повільний
 * дзвінок, ніж жодного.
 */
export async function solvePowOffThread(
  challenge: string,
  difficulty: number,
  options: { maxAttempts?: number; maxDurationMs?: number } = {},
): Promise<SolveResult> {
  if (typeof Worker === 'undefined') {
    return solvePow(challenge, difficulty, options);
  }

  let worker: Worker;
  try {
    worker = new Worker(new URL('./pow-worker.ts', import.meta.url), { type: 'module' });
  } catch {
    return solvePow(challenge, difficulty, options);
  }

  try {
    return await new Promise<SolveResult>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<PowWorkerResponse>): void => {
        const data = event.data;
        if (data.status === 'failed') {
          reject(new Error(data.reason));
          return;
        }
        resolve(data);
      };
      worker.onerror = (): void => {
        reject(new Error('worker підбору nonce впав'));
      };
      const request: PowWorkerRequest = {
        challenge,
        difficulty,
        ...(options.maxAttempts === undefined ? {} : { maxAttempts: options.maxAttempts }),
        ...(options.maxDurationMs === undefined ? {} : { maxDurationMs: options.maxDurationMs }),
      };
      worker.postMessage(request);
    });
  } catch {
    // Падіння воркера не має ламати дзвінок — доганяємо на головному потоці.
    return await solvePow(challenge, difficulty, options);
  } finally {
    worker.terminate();
  }
}
